import {Vector2, Vector3} from "@cm/math"
import {Three as THREE, ThreeMeshBvh} from "@cm/material-nodes/three"
import type {CmLogger} from "@cm/log"
import type {ReifiedMeshData, MeshBuffers, MeshPointData} from "#template/interfaces/mesh-data"
import {createMeshBuffersForMaterialGroups} from "#template/interfaces/mesh-data"
import type {IMeshGeometryQueries} from "#template/interfaces/scene-manager"
import {EMPTY, type Observable} from "rxjs"

THREE.BufferGeometry.prototype.computeBoundsTree = ThreeMeshBvh.computeBoundsTree
THREE.BufferGeometry.prototype.disposeBoundsTree = ThreeMeshBvh.disposeBoundsTree
THREE.Mesh.prototype.raycast = ThreeMeshBvh.acceleratedRaycast

class LeastRecentlyUsedCache<KeyT extends string, ValueT> {
    private cache: Map<KeyT, ValueT> = new Map()
    private keys: KeyT[] = []

    constructor(private size: number) {}

    get(key: KeyT): ValueT | undefined {
        const value = this.cache.get(key)
        if (value) {
            this.keys = this.keys.filter((k) => k !== key)
            this.keys.unshift(key)
        }
        return value
    }

    set(key: KeyT, value: ValueT): void {
        if (this.cache.size >= this.size) {
            const keyToRemove = this.keys.pop()
            if (keyToRemove) this.cache.delete(keyToRemove)
        }
        this.cache.set(key, value)
        this.keys.unshift(key)
    }
}

type Dim = 2 | 3
type Attribute<D extends Dim> = D extends 2 ? Vector2 : Vector3

export function barycentricCoordinates(point: Vector2, triangle: [Vector2, Vector2, Vector2]): {u: number; v: number}
export function barycentricCoordinates(point: Vector3, triangle: [Vector3, Vector3, Vector3]): {u: number; v: number}
export function barycentricCoordinates(point: Vector2 | Vector3, triangle: [Vector2, Vector2, Vector2] | [Vector3, Vector3, Vector3]): {u: number; v: number} {
    const {p, t} = (() => {
        if (point instanceof Vector2 && triangle[0] instanceof Vector2 && triangle[1] instanceof Vector2 && triangle[2] instanceof Vector2) {
            return {p: new Vector3(point.x, point.y, 0), t: triangle.map((v) => new Vector3(v.x, v.y, 0)) as [Vector3, Vector3, Vector3]}
        } else if (point instanceof Vector3 && triangle[0] instanceof Vector3 && triangle[1] instanceof Vector3 && triangle[2] instanceof Vector3) {
            return {p: point, t: triangle as [Vector3, Vector3, Vector3]}
        } else {
            throw new Error("Invalid input")
        }
    })()

    const [a, b, c] = t

    const v0 = c.sub(a)
    const v1 = b.sub(a)
    const v2 = p.sub(a)

    const dot00 = v0.dot(v0)
    const dot01 = v0.dot(v1)
    const dot02 = v0.dot(v2)
    const dot11 = v1.dot(v1)
    const dot12 = v1.dot(v2)

    const invDenom = 1 / (dot00 * dot11 - dot01 * dot01)
    const u = (dot00 * dot12 - dot01 * dot02) * invDenom
    const v = (dot11 * dot02 - dot01 * dot12) * invDenom

    return {u, v}
}

export function interpolateAttribute<D extends Dim>(
    attribute: Float32Array,
    attributeDim: D,
    idx0: number,
    idx1: number,
    idx2: number,
    weights: {u: number; v: number},
): Attribute<D> {
    if (attributeDim === 2) {
        const value0 = new Vector2(attribute[idx0 * attributeDim], attribute[idx0 * attributeDim + 1])
        const value1 = new Vector2(attribute[idx1 * attributeDim], attribute[idx1 * attributeDim + 1])
        const value2 = new Vector2(attribute[idx2 * attributeDim], attribute[idx2 * attributeDim + 1])
        return value0
            .mul(1 - weights.u - weights.v)
            .add(value1.mul(weights.u))
            .add(value2.mul(weights.v)) as Attribute<D>
    } else {
        const value0 = new Vector3(attribute[idx0 * attributeDim], attribute[idx0 * attributeDim + 1], attribute[idx0 * attributeDim + 2])
        const value1 = new Vector3(attribute[idx1 * attributeDim], attribute[idx1 * attributeDim + 1], attribute[idx1 * attributeDim + 2])
        const value2 = new Vector3(attribute[idx2 * attributeDim], attribute[idx2 * attributeDim + 1], attribute[idx2 * attributeDim + 2])
        return value0
            .mul(1 - weights.u - weights.v)
            .add(value1.mul(weights.u))
            .add(value2.mul(weights.v)) as Attribute<D>
    }
}

// Maximum UV-space distance from the mesh surface that still counts as on-surface.
// UV tessellation at rounded corners can leave sub-millimetre gaps due to floating-point
// imprecision, so control points near the boundary may fall fractionally outside every triangle.
const UV_ON_SURFACE_TOLERANCE = 1e-3

type SubMesh = {geometry: THREE.BufferGeometry; materialIndex: number}

function closestHit<T extends SubMesh>(subMeshes: T[], point: THREE.Vector3) {
    const hits = subMeshes.flatMap((subMesh) => {
        const bvh = subMesh.geometry.boundsTree
        if (!(bvh instanceof ThreeMeshBvh.MeshBVH)) return []
        const info = bvh.closestPointToPoint(point)
        if (info === null) return []
        const {
            face: {a: vertex0, b: vertex1, c: vertex2},
        } = ThreeMeshBvh.getTriangleHitPointInfo(point, subMesh.geometry, info.faceIndex)
        return [{subMesh, distance: info.distance, point: info.point, vertex0, vertex1, vertex2}]
    })
    return hits.sort((a, b) => a.distance - b.distance).at(0)
}

export class MeshGeometryQueries implements IMeshGeometryQueries {
    private readonly cache = new LeastRecentlyUsedCache<string, (SubMesh & {buffers: MeshBuffers})[]>(4)

    constructor(private readonly logger?: CmLogger) {}

    raycastScene(_origin: Vector3, _direction: Vector3): number | null {
        return null
    }

    sceneGeometryChanged(): Observable<void> {
        return EMPTY
    }

    // the sub-meshes ThreeMesh builds; without a UV channel they lie in position space
    private subMeshes(mesh: ReifiedMeshData, uvChannel: number | undefined) {
        const key = `uvChannel:${uvChannel ?? "none"}/meshContentHash:${mesh.contentHash}`
        const cached = this.cache.get(key)
        if (cached) return cached

        const subMeshes = createMeshBuffersForMaterialGroups(mesh)
            .filter((buffers) => buffers.indices.length > 0)
            .map((buffers) => {
                const geometry = new THREE.BufferGeometry()
                if (uvChannel === undefined) {
                    geometry.setAttribute("position", new THREE.BufferAttribute(buffers.vertices, 3))
                    geometry.setAttribute("normal", new THREE.BufferAttribute(buffers.normals, 3))
                    buffers.uvs.forEach((uv, uvIdx) => geometry.setAttribute(uvIdx === 0 ? "uv" : `uv${uvIdx}`, new THREE.BufferAttribute(uv, 2)))
                } else {
                    const uv = buffers.uvs[uvChannel]
                    const uvPositions = new Float32Array((uv.length / 2) * 3)
                    for (let vertex = 0; vertex < uv.length / 2; vertex++) {
                        uvPositions[vertex * 3] = uv[vertex * 2]
                        uvPositions[vertex * 3 + 1] = uv[vertex * 2 + 1]
                    }
                    geometry.setAttribute("position", new THREE.BufferAttribute(uvPositions, 3))
                }
                geometry.setIndex(new THREE.BufferAttribute(buffers.indices, 1))
                geometry.computeBoundsTree()
                return {geometry, materialIndex: buffers.materialIndex, buffers}
            })

        this.cache.set(key, subMeshes)
        return subMeshes
    }

    projectPointToMeshInUVSpace(mesh: ReifiedMeshData, meshUVChannel: number, pointUV: Vector2): MeshPointData | null {
        if (mesh.uvs.length <= meshUVChannel) return null

        const hit = closestHit(this.subMeshes(mesh, meshUVChannel), new THREE.Vector3(pointUV.x, pointUV.y, 0))
        if (!hit || hit.distance > UV_ON_SURFACE_TOLERANCE) return null

        const {buffers} = hit.subMesh
        const {vertex0, vertex1, vertex2} = hit
        const meshUV = buffers.uvs[meshUVChannel]
        const faceUV0 = Vector2.fromArray([meshUV[vertex0 * 2], meshUV[vertex0 * 2 + 1]])
        const faceUV1 = Vector2.fromArray([meshUV[vertex1 * 2], meshUV[vertex1 * 2 + 1]])
        const faceUV2 = Vector2.fromArray([meshUV[vertex2 * 2], meshUV[vertex2 * 2 + 1]])
        const weights = barycentricCoordinates(pointUV, [faceUV0, faceUV1, faceUV2])

        return {
            position: interpolateAttribute(buffers.vertices, 3, vertex0, vertex1, vertex2, weights),
            normal: interpolateAttribute(buffers.normals, 3, vertex0, vertex1, vertex2, weights).normalized(),
            uvs: buffers.uvs.map((uv) => interpolateAttribute(uv, 2, vertex0, vertex1, vertex2, weights)),
            materialID: buffers.materialIndex,
        }
    }

    projectPointToMeshInPositionSpace(mesh: ReifiedMeshData, point: Vector3, prebuiltSubMeshes?: SubMesh[]): MeshPointData | null {
        const subMeshes = prebuiltSubMeshes && prebuiltSubMeshes.length > 0 ? prebuiltSubMeshes : this.subMeshes(mesh, undefined)
        const hit = closestHit(subMeshes, new THREE.Vector3(point.x, point.y, point.z))
        if (!hit) return null

        const {geometry, materialIndex} = hit.subMesh
        const {vertex0, vertex1, vertex2} = hit

        const positions = geometry.getAttribute("position").array
        if (!(positions instanceof Float32Array)) throw new Error(`Expected Float32Array, got ${positions.constructor.name}`)
        const position0 = new Vector3(positions[3 * vertex0], positions[3 * vertex0 + 1], positions[3 * vertex0 + 2])
        const position1 = new Vector3(positions[3 * vertex1], positions[3 * vertex1 + 1], positions[3 * vertex1 + 2])
        const position2 = new Vector3(positions[3 * vertex2], positions[3 * vertex2 + 1], positions[3 * vertex2 + 2])

        const meshPoint = Vector3.fromArray(hit.point.toArray())
        const weights = barycentricCoordinates(meshPoint, [position0, position1, position2])

        if (weights.u < 0 || weights.v < 0 || weights.u + weights.v > 1) {
            this.logger?.warn(`Projection ${meshPoint} for point ${point} with weights ${weights} outside of triangle ${position0}, ${position1}, ${position2}`)
        }

        const normals = geometry.getAttribute("normal").array
        if (!(normals instanceof Float32Array)) throw new Error(`Expected Float32Array, got ${normals.constructor.name}`)
        const uvs = mesh.uvs.map((_, i) => {
            const uv = geometry.getAttribute(i === 0 ? "uv" : `uv${i}`).array
            if (!(uv instanceof Float32Array)) throw new Error(`Expected Float32Array, got ${uv.constructor.name}`)
            return uv
        })

        return {
            position: interpolateAttribute(positions, 3, vertex0, vertex1, vertex2, weights),
            normal: interpolateAttribute(normals, 3, vertex0, vertex1, vertex2, weights).normalized(),
            uvs: uvs.map((uv) => interpolateAttribute(uv, 2, vertex0, vertex1, vertex2, weights)),
            materialID: materialIndex,
        }
    }
}
